'use memo'

import { useState } from 'react'
import {
  type CrmAgencyConnectionProviderResponse,
  type CrmAgencyConnectionProviderResponseProvider,
  CrmAgencyConnectionProviderResponseStatus,
} from '@urbanx/agx.crm.client'
import {
  AgxSpacer,
  AgxTypography,
  AgxBadge,
  AgxButton,
  AgxTextInput,
  Spinner,
} from '@urbanx/agx.ui-components2'
import CrmIcon from './CrmIcon'
import { getEditableFields } from './helpers'
import './CrmCard.scss'

interface CrmCardProps {
  activeProvider?: CrmAgencyConnectionProviderResponseProvider
  busyProvider: CrmAgencyConnectionProviderResponseProvider | null
  providerDetails: CrmAgencyConnectionProviderResponse
  requestPending: boolean
  onConnect: (
    provider: CrmAgencyConnectionProviderResponseProvider,
    fieldValues: Record<string, string>
  ) => Promise<boolean>
  onDisconnect: (provider: CrmAgencyConnectionProviderResponseProvider) => void
  onDirtyChange: (
    provider: CrmAgencyConnectionProviderResponseProvider,
    dirty: boolean
  ) => void
}

const CrmCard = ({
  activeProvider,
  busyProvider,
  providerDetails,
  requestPending,
  onConnect,
  onDisconnect,
  onDirtyChange,
}: CrmCardProps) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  if (!providerDetails.provider) return null

  const crmProvider = providerDetails.provider
  const fields = providerDetails.fields ?? []
  const connectionStatus =
    providerDetails.status ??
    CrmAgencyConnectionProviderResponseStatus.NotConnected
  const providerName = providerDetails.displayName || crmProvider
  const connected =
    connectionStatus === CrmAgencyConnectionProviderResponseStatus.Connected
  const connectable = providerDetails.connectable === true
  const disabledByActiveProvider =
    Boolean(activeProvider) && activeProvider !== crmProvider
  const busy = busyProvider === crmProvider
  const disabled =
    disabledByActiveProvider ||
    (!connected && !connectable) ||
    (requestPending && !busy)
  const editableFields = getEditableFields(fields)
  const connectFieldValues = Object.fromEntries(
    editableFields.map((field) => [
      field.name!,
      drafts[field.name!]?.trim() ?? '',
    ])
  ) as Record<string, string>
  const canConnect =
    !disabled &&
    connectable &&
    editableFields.length > 0 &&
    Object.values(connectFieldValues).every(Boolean)

  const actionText = connected ? 'Disconnect' : 'Connect'
  const actionDisabled = connected ? disabled : disabled || !canConnect
  const setDraft = (fieldName: string, value: string) => {
    const nextDrafts = { ...drafts, [fieldName]: value }

    setDrafts(nextDrafts)
    onDirtyChange(
      crmProvider,
      Object.values(nextDrafts).some((draftValue) => Boolean(draftValue.trim()))
    )
  }
  const connect = () => {
    void onConnect(crmProvider, connectFieldValues).then((connectedProvider) => {
      if (!connectedProvider) return

      setDrafts({})
      onDirtyChange(crmProvider, false)
    })
  }

  return (
    <AgxSpacer
      direction="vertical"
      padding={{
        top: 'sm',
        bottom: 'sm',
        right: 'sm',
        left: 'sm',
      }}
      gap="sm"
      style={{
        borderRadius: 'var(--corner-sm, 16px)',
        background: 'var(--bg-secondary, #FFF)',
        boxShadow:
          '0 1px 4px 1px rgba(0, 0, 0, 0.08), 0 2px 3px 1px rgba(0, 0, 0, 0.04)',
      }}
      fill
      contentJustification="space-between"
    >
      <AgxSpacer direction="vertical" gap="sm" fill>
        <AgxBadge
          type="colourfill"
          size="sm"
          colour={
            connectionStatus ===
            CrmAgencyConnectionProviderResponseStatus.Connected
              ? 'green'
              : 'grey'
          }
          text={
            connectionStatus ===
            CrmAgencyConnectionProviderResponseStatus.Connected
              ? 'Active'
              : 'Not connected'
          }
        />
        <AgxSpacer
          direction="horizontal"
          gap="spacing-lg"
          contentAlignment="center"
        >
          <CrmIcon crmProvider={crmProvider} />
          <AgxTypography
            id={`${crmProvider}-name`}
            type={'body_ui_strong'}
            size={'medium'}
            color="var(--text-primary)"
          >
            {providerName}
          </AgxTypography>
        </AgxSpacer>

        {fields.length > 0 && (
          <AgxSpacer direction="vertical" gap="spacing-sm" fill>
            {fields.map((field) => {
              const fieldName = field.name || 'id'
              const draftValue = drafts[fieldName] ?? ''
              const fieldValue = connected ? field.value || '' : draftValue

              return (
                <AgxTextInput
                  id={`${crmProvider}-${fieldName}`}
                  key={`${crmProvider}-${fieldName}`}
                  type="text"
                  label={field.label || 'ID'}
                  required
                  hideOptionalText
                  defaultValue={fieldValue}
                  disabled={disabled || busy || field.editable === false}
                  readonly={connected}
                  onChange={(value) => setDraft(fieldName, value)}
                  stretch
                />
              )
            })}
          </AgxSpacer>
        )}
        {!connectable && !connected && (
          <AgxTypography
            id={`${crmProvider}-coming-soon-label`}
            type="label"
            size="small"
          >
            Coming Soon
          </AgxTypography>
        )}
      </AgxSpacer>

      <AgxButton
        id={`${crmProvider}-${connected ? 'disconnect' : 'connect'}`}
        size="small"
        variant={connected ? 'hollow' : 'primary'}
        leftIcon={
          busy ? (
            <Spinner className="crmCard__buttonSpinner" size={16} />
          ) : undefined
        }
        text={busy ? (connected ? 'Disconnecting' : 'Connecting') : actionText}
        disabled={actionDisabled || busy}
        onClick={
          connected ? () => onDisconnect(crmProvider) : connect
        }
        stretch
      />
    </AgxSpacer>
  )
}

export default CrmCard
